import { useState, useEffect } from 'react';
import { getProfile, getUserPeethProgress } from '../services/api';
import { triggerToast } from '../components/Toast';
import { MILESTONES } from '../game/constants';
import AnimatedCounter from '../components/AnimatedCounter';
import HanumanHero from '../components/HanumanHero';
import ShaktiPeethIllustration from '../components/PeethIllustration';
import { hanumanJi } from '../assets/images';
import BackButton from '../components/BackButton';
import { useLang, localizePeeth } from '../i18n';

const TOTAL_PEETHS = 108;

function fmtBadgeCount(n) {
  if (n >= 100000) return (n / 100000).toFixed(1).replace(/\.0$/, '') + 'L';
  if (n >= 1000)   return (n / 1000) + 'K';
  return String(n);
}

function fmtDate(iso, locale = 'hi-IN') {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ProfileScreen({ user, isGuest, onBack }) {
  const { lang, t } = useLang();
  const dateLocale = lang === 'mr' ? 'mr-IN' : lang === 'en' ? 'en-IN' : 'hi-IN';
  const [profile, setProfile] = useState(user);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isGuest) { setLoading(false); return; }
    async function load() {
      try {
        const [profileData, progressData] = await Promise.all([
          getProfile().catch(() => null),
          getUserPeethProgress().catch(() => null),
        ]);
        if (profileData?.user) setProfile(profileData.user);
        if (progressData) setProgress(progressData);
      } catch {
        triggerToast(t('profile.loadError'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isGuest]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalRam         = profile?.total_ram_count || 0;
  const earnedMilestones = MILESTONES.filter(m => totalRam >= m.count);
  const peethsVisited    = profile?.shakti_peeths_visited || 0;
  const peethPercent     = Math.min(100, Math.round((peethsVisited / TOTAL_PEETHS) * 100));

  // SVG progress ring math
  const ringRadius        = 46;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset        = ringCircumference - (peethPercent / 100) * ringCircumference;

  if (isGuest) {
    return (
      <div className="screen" style={{ justifyContent: 'center', textAlign: 'center', padding: 24 }}>
        <HanumanHero size="small" />
        <p className="app-subtitle" style={{ marginTop: 16 }}>{t('profile.guestPrompt')}</p>
        <button className="btn btn-secondary" style={{ marginTop: 32 }} onClick={onBack}>
          {t('profile.goBack')}
        </button>
      </div>
    );
  }

  return (
    <div className="screen profile-screen" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div className="screen-header">
        <BackButton onClick={onBack} />
        <div className="screen-header-title">{t('profile.title')}</div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div className="spinner" />
        </div>
      ) : (
        <div className="profile-scroll">
          {/* ── Hero Identity Card ───────────────────────────── */}
          <div className="profile-hero">
            <div className="profile-hero-bg" />
            <div className="profile-hero-content">
              <div className="profile-avatar-frame">
                <div className="profile-avatar-glow" />
                <img
                  src={hanumanJi}
                  alt="Avatar"
                  className="profile-avatar"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
              <div className="profile-name">{profile?.name || t('profile.defaultName')}</div>
              <div className="profile-email">{profile?.email}</div>
              {(profile?.city || profile?.state) && (
                <div className="profile-location">
                  <span className="profile-location-dot" />
                  {profile.city}{profile.state ? `, ${profile.state}` : ''}
                </div>
              )}
              <div className="profile-member-since">
                {t('profile.memberSince', { date: fmtDate(profile?.created_at, dateLocale) })}
              </div>
            </div>
          </div>

          {/* ── Peeth Progress Ring + Stats ──────────────────── */}
          <div className="profile-progress-card">
            <div className="profile-ring-wrap">
              <svg width="120" height="120" viewBox="0 0 120 120">
                <defs>
                  <linearGradient id="progressGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%"   stopColor="#FFB347" />
                    <stop offset="50%"  stopColor="#FF6B00" />
                    <stop offset="100%" stopColor="#D4AF37" />
                  </linearGradient>
                </defs>
                <circle className="profile-ring-track"
                  cx="60" cy="60" r={ringRadius} strokeWidth="9" fill="none" />
                <circle className="profile-ring-progress"
                  cx="60" cy="60" r={ringRadius} strokeWidth="9" fill="none"
                  strokeLinecap="round"
                  strokeDasharray={ringCircumference}
                  strokeDashoffset={ringOffset}
                  transform="rotate(-90 60 60)" />
              </svg>
              <div className="profile-ring-center">
                <div className="profile-ring-pct">{peethPercent}%</div>
                <div className="profile-ring-detail">{t('profile.peethsCount', { n: peethsVisited, total: TOTAL_PEETHS })}</div>
              </div>
            </div>

            <div className="profile-stats-pills">
              <div className="profile-stat-pill" style={{ '--i': 0 }}>
                <div className="profile-stat-pill-icon" data-tone="saffron">🔆</div>
                <div className="profile-stat-pill-body">
                  <AnimatedCounter value={totalRam} duration={1100} className="profile-stat-pill-value" />
                  <div className="profile-stat-pill-label">{t('profile.statTotalRam')}</div>
                </div>
              </div>
              <div className="profile-stat-pill" style={{ '--i': 1 }}>
                <div className="profile-stat-pill-icon" data-tone="gold">★</div>
                <div className="profile-stat-pill-body">
                  <AnimatedCounter value={earnedMilestones.length} duration={1000} className="profile-stat-pill-value" />
                  <div className="profile-stat-pill-label">{t('profile.statBadges')}</div>
                </div>
              </div>
              <div className="profile-stat-pill" style={{ '--i': 2 }}>
                <div className="profile-stat-pill-icon" data-tone="maroon">⏱</div>
                <div className="profile-stat-pill-body">
                  <div className="profile-stat-pill-value">{fmtDate(profile?.last_active, dateLocale)}</div>
                  <div className="profile-stat-pill-label">{t('profile.statLastActive')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Badges ──────────────────────────────────────── */}
          <div className="profile-section">
            <div className="profile-section-header">
              <div className="profile-section-title">{t('profile.sectionBadges')}</div>
              <div className="profile-section-meta">{earnedMilestones.length}/{MILESTONES.length}</div>
            </div>
            <div className="profile-badges-grid">
              {MILESTONES.map((m, idx) => {
                const earned = totalRam >= m.count;
                return (
                  <div
                    key={m.count}
                    className={`profile-badge ${earned ? 'earned' : 'locked'}`}
                    style={{ '--i': idx }}
                    title={earned ? m.badge : t('profile.badgeUnlockAt', { ram: fmtBadgeCount(m.count) })}
                  >
                    <div className="profile-badge-shimmer" />
                    <svg className="profile-badge-medal" width="34" height="34" viewBox="0 0 36 36" aria-hidden="true">
                      <defs>
                        <linearGradient id={`medalGrad-${idx}`} x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%"   stopColor={earned ? '#FFE48A' : '#5C3A1A'} />
                          <stop offset="60%"  stopColor={earned ? '#D4AF37' : '#3D2A14'} />
                          <stop offset="100%" stopColor={earned ? '#A6822A' : '#2A1A0A'} />
                        </linearGradient>
                      </defs>
                      {/* ribbon tails */}
                      <path d="M10 22 L7 34 L13 30 L15 22 Z" fill={earned ? '#9B0030' : '#3A1010'} opacity={earned ? 1 : 0.5} />
                      <path d="M26 22 L29 34 L23 30 L21 22 Z" fill={earned ? '#9B0030' : '#3A1010'} opacity={earned ? 1 : 0.5} />
                      {/* medallion */}
                      <circle cx="18" cy="16" r="12" fill={`url(#medalGrad-${idx})`} />
                      <circle cx="18" cy="16" r="9.5" fill="none" stroke={earned ? '#FFF2C2' : '#5A3A1A'} strokeWidth="0.6" opacity="0.55" />
                      <text x="18" y="20" textAnchor="middle" fontFamily="serif" fontSize="11" fontWeight="700"
                        fill={earned ? '#5C0010' : '#1A0A05'}>
                        {fmtBadgeCount(m.count)}
                      </text>
                    </svg>
                    <div className="profile-badge-label">{fmtBadgeCount(m.count)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Recent Unlocks ──────────────────────────────── */}
          {progress && progress.progress && progress.progress.length > 0 && (
            <div className="profile-section">
              <div className="profile-section-header">
                <div className="profile-section-title">{t('profile.sectionRecent')}</div>
                <div className="profile-section-meta">{progress.progress.length}</div>
              </div>
              <div className="profile-unlocks-list">
                {progress.progress.slice(-6).reverse().map((p, idx) => (
                  <div key={p.peeth_id} className="profile-unlock-row" style={{ '--i': idx }}>
                    <div className="profile-unlock-thumb">
                      <ShaktiPeethIllustration
                        peethId={p.peeth_id}
                        state={p.state}
                        country={p.country}
                      />
                    </div>
                    <div className="profile-unlock-body">
                      <div className="profile-unlock-num">#{p.peeth_id}</div>
                      <div className="profile-unlock-name">{localizePeeth(p, lang).name}</div>
                      <div className="profile-unlock-location">{p.location}</div>
                    </div>
                    <div className="profile-unlock-date">
                      {fmtDate(p.unlocked_at, dateLocale)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ height: 24 }} />
        </div>
      )}
    </div>
  );
}
